import { useEffect, useReducer, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { DashboardState } from "@/components/dashboard/DashboardState";
import { FloatingToast } from "@/components/feedback/FloatingToast";
import type { CurrentUser } from "@/features/auth/currentUserTypes";
import { getOperationalCapabilities } from "@/features/auth/operationalCapabilities";
import { useDashboardV2PeriodQueries } from "@/features/dashboard/hooks";
import { useBuyerStorePages } from "@/features/store/hooks";
import {
  createDashboardPeriodState,
  dashboardPeriodLabel,
  type DashboardPeriodState,
} from "@/features/dashboard/period";
import type {
  DashboardAnalyticsResponse,
  DashboardOperationsResponse,
} from "@/features/dashboard/types";
import {
  CheckerDashboard,
  type CheckerDashboardPanelRequest,
} from "./CheckerDashboard";
import {
  ClientDashboard,
  StoreSwitch,
  type ClientDashboardPanelRequest,
} from "./ClientDashboard";
import {
  DashboardDrilldownHost,
  type DashboardCategoryPanelRequest,
} from "./DashboardDrilldownHost";
import {
  OverviewerDashboard,
  type OverviewerDashboardPanelRequest,
} from "./OverviewerDashboard";
import { AdminHelperDashboard } from "./AdminHelperDashboard";
import {
  SuperDashboard,
  type SuperDashboardPanelRequest,
} from "./SuperDashboard";
import {
  SupplierDashboard,
  type SupplierDashboardPanelRequest,
} from "./SupplierDashboard";
import {
  dashboardPeriodTransition,
  initialDashboardPeriodTransition,
} from "./dashboardPeriodTransition";

export type DashboardPanelRequest =
  | SuperDashboardPanelRequest
  | ClientDashboardPanelRequest
  | SupplierDashboardPanelRequest
  | CheckerDashboardPanelRequest
  | OverviewerDashboardPanelRequest
  | DashboardCategoryPanelRequest;

interface DashboardPanelState {
  request: DashboardPanelRequest | null;
  open: boolean;
}

type DashboardNavigationDestination =
  | "/pedidos"
  | "/cargas"
  | { to: "/pedidos" | "/cargas" }
  | {
      to: "/cotacoes/$quotationId";
      params: { quotationId: string };
    };

const responseMatchesUser = (
  user: CurrentUser,
  response: DashboardAnalyticsResponse | DashboardOperationsResponse,
) => response.role === user.role;

const responseMatchesPeriod = (
  response: DashboardAnalyticsResponse,
  period: DashboardPeriodState,
) =>
  response.period.kind === period.period &&
  response.period.requested_history === period.history &&
  (period.period !== "custom" ||
    (response.period.from === period.from && response.period.to === period.to));

function DashboardOperationsSummary({
  operations,
}: {
  operations: DashboardOperationsResponse;
}) {
  const count = operations.open_loads.total;

  return (
    <section
      data-testid="dashboard-operations-summary"
      aria-label="Operações atuais"
    >
      <h2>Operações atuais</h2>
      <p>{count.toLocaleString("pt-BR")} cargas abertas</p>
    </section>
  );
}

function renderRoleDashboard({
  user,
  analytics,
  operations,
  period,
  pendingPeriod,
  updating,
  onPeriodChange,
  onNavigate,
  onOpenPanel,
  onRetryAnalytics,
  onRetryOperations,
  selectedStoreId,
  authorizedStores,
  hasMoreStores,
  loadingMoreStores,
  onLoadMoreStores,
  onSelectStore,
}: {
  user: CurrentUser;
  analytics: DashboardAnalyticsResponse;
  operations?: DashboardOperationsResponse;
  period: DashboardPeriodState;
  pendingPeriod: DashboardPeriodState | null;
  updating: boolean;
  onPeriodChange: (period: DashboardPeriodState) => void;
  onNavigate: (destination: DashboardNavigationDestination) => void;
  onOpenPanel: (request: DashboardPanelRequest) => void;
  onRetryAnalytics: () => void;
  onRetryOperations: () => void;
  selectedStoreId?: string;
  authorizedStores?: { id: string; name: string }[];
  hasMoreStores?: boolean;
  loadingMoreStores?: boolean;
  onLoadMoreStores?: () => void;
  onSelectStore?: (id: string) => void;
}): ReactNode {
  switch (user.role) {
    case "super":
      if (
        analytics.role !== "super" ||
        (operations?.role !== "super" && operations)
      )
        return null;
      return (
        <SuperDashboard
          user={user}
          analytics={analytics}
          operations={operations}
          period={period}
          pendingPeriod={pendingPeriod}
          updating={updating}
          onPeriodChange={onPeriodChange}
          onNavigate={onNavigate}
          onOpenPanel={onOpenPanel}
          onRetryAnalytics={onRetryAnalytics}
        />
      );
    case "client":
      if (
        analytics.role !== "client" ||
        (operations?.role !== "client" && operations)
      )
        return null;
      return (
        <ClientDashboard
          user={user}
          analytics={analytics}
          operations={operations}
          period={period}
          pendingPeriod={pendingPeriod}
          updating={updating}
          onPeriodChange={onPeriodChange}
          onNavigate={onNavigate}
          onOpenPanel={onOpenPanel}
          onRetryAnalytics={onRetryAnalytics}
        />
      );
    case "client_store":
      if (
        analytics.role !== "client_store" ||
        (operations?.role !== "client_store" && operations)
      )
        return null;
      return (
        <ClientDashboard
          user={user}
          selectedStoreId={selectedStoreId}
          authorizedStores={authorizedStores}
          hasMoreStores={hasMoreStores}
          loadingMoreStores={loadingMoreStores}
          onLoadMoreStores={onLoadMoreStores}
          onSelectStore={onSelectStore}
          analytics={analytics}
          operations={operations}
          period={period}
          pendingPeriod={pendingPeriod}
          updating={updating}
          onPeriodChange={onPeriodChange}
          onNavigate={onNavigate}
          onOpenPanel={onOpenPanel}
          onRetryAnalytics={onRetryAnalytics}
        />
      );
    case "supplier":
      if (
        analytics.role !== "supplier" ||
        (operations?.role !== "supplier" && operations)
      )
        return null;
      return (
        <SupplierDashboard
          user={user}
          analytics={analytics}
          operations={operations}
          period={period}
          pendingPeriod={pendingPeriod}
          updating={updating}
          onPeriodChange={onPeriodChange}
          onNavigate={onNavigate}
          onOpenPanel={onOpenPanel}
          onRetryAnalytics={onRetryAnalytics}
          onRetryOperations={onRetryOperations}
        />
      );
    case "checker":
      if (
        analytics.role !== "checker" ||
        (operations?.role !== "checker" && operations)
      )
        return null;
      return (
        <CheckerDashboard
          user={user}
          analytics={analytics}
          operations={operations}
          period={period}
          pendingPeriod={pendingPeriod}
          updating={updating}
          onPeriodChange={onPeriodChange}
          onNavigate={(destination) => onNavigate(destination)}
          onOpenPanel={onOpenPanel}
          onRetryAnalytics={onRetryAnalytics}
        />
      );
    case "overviewer":
      if (
        analytics.role !== "overviewer" ||
        (operations?.role !== "overviewer" && operations)
      )
        return null;
      return (
        <OverviewerDashboard
          user={user}
          analytics={analytics}
          operations={operations}
          period={period}
          pendingPeriod={pendingPeriod}
          updating={updating}
          onPeriodChange={onPeriodChange}
          onNavigate={(destination) => onNavigate(destination)}
          onOpenPanel={onOpenPanel}
          onRetryAnalytics={onRetryAnalytics}
        />
      );
    case "admin_helper":
      if (
        analytics.role !== "admin_helper" ||
        (operations?.role !== "admin_helper" && operations)
      )
        return null;
      return (
        <AdminHelperDashboard
          user={user}
          analytics={analytics}
          operations={operations}
          period={period}
          pendingPeriod={pendingPeriod}
          updating={updating}
          canManageQuotation={
            getOperationalCapabilities(user).canManageQuotation
          }
          onPeriodChange={onPeriodChange}
          onNavigate={(destination) => onNavigate(destination)}
          onRetryAnalytics={onRetryAnalytics}
        />
      );
    default:
      return null;
  }
}

export function DashboardPage({ user }: { user: CurrentUser }) {
  const navigate = useNavigate();
  const defaultPeriodRef = useRef(
    createDashboardPeriodState(
      ["super", "client", "client_store", "supplier"].includes(user.role)
        ? { history: 8 }
        : {},
    ),
  );
  const attemptID = useRef(1);
  const [periodState, dispatchPeriod] = useReducer(
    dashboardPeriodTransition,
    { id: 1, period: defaultPeriodRef.current },
    initialDashboardPeriodTransition,
  );
  const [panel, setPanel] = useState<DashboardPanelState>({
    request: null,
    open: false,
  });
  const storePages = useBuyerStorePages(user.role === "client_store");
  const authorizedStores = (
    storePages.data?.pages.flatMap((page) => page.items) ?? []
  ).filter(
    ({ id, client_id }) =>
      client_id === user.client_id &&
      (user.store_access?.scope_type === "all" ||
        user.store_access?.store_ids.includes(id)),
  );
  const [chosenStoreId, setChosenStoreId] = useState("");
  const selectedStoreId =
    user.role === "client_store"
      ? (
          authorizedStores.find(({ id }) => id === chosenStoreId) ??
          authorizedStores[0]
        )?.id
      : undefined;
  const setPanelRequest = (request: DashboardPanelRequest | null) => {
    setPanel((current) =>
      request
        ? { request, open: true }
        : current.request
          ? { ...current, open: false }
          : current,
    );
  };
  const finishPanelClose = () => {
    setPanel((current) =>
      current.open ? current : { request: null, open: false },
    );
  };
  const candidatePeriod =
    periodState.pending?.period ??
    periodState.failed?.period ??
    defaultPeriodRef.current;
  const queries = useDashboardV2PeriodQueries({
    user,
    activePeriod: periodState.activePeriod,
    candidatePeriod,
    candidateEnabled: periodState.pending !== null,
    selectedStoreId,
  });
  const scopeError =
    user.role === "client_store" &&
    (storePages.isError ||
      (storePages.data &&
        authorizedStores.length === 0 &&
        !storePages.hasNextPage))
      ? new Error("Loja autorizada indisponível")
      : null;
  const activeAnalyticsResponse = queries.activeAnalytics.data;
  const candidateAnalyticsResponse = queries.candidateAnalytics.data;
  const operationsResponse = queries.operations.data;
  const analytics =
    queries.scopeReady &&
    periodState.activePeriod &&
    activeAnalyticsResponse &&
    !queries.activeAnalytics.isPlaceholderData &&
    responseMatchesUser(user, activeAnalyticsResponse) &&
    responseMatchesPeriod(activeAnalyticsResponse, periodState.activePeriod)
      ? activeAnalyticsResponse
      : undefined;
  const operations =
    queries.scopeReady &&
    operationsResponse &&
    responseMatchesUser(user, operationsResponse)
      ? operationsResponse
      : undefined;
  const activeAnalyticsContractError =
    activeAnalyticsResponse &&
    !queries.activeAnalytics.isPlaceholderData &&
    !analytics
      ? new TypeError("dashboard active analytics contract mismatch")
      : null;
  const candidateAnalyticsContractError =
    candidateAnalyticsResponse &&
    !queries.candidateAnalytics.isPlaceholderData &&
    (!responseMatchesUser(user, candidateAnalyticsResponse) ||
      !responseMatchesPeriod(candidateAnalyticsResponse, candidatePeriod))
      ? new TypeError("dashboard candidate analytics contract mismatch")
      : null;
  const operationsRoleError =
    operationsResponse && !operations
      ? new TypeError("dashboard operations role mismatch")
      : null;

  useEffect(() => {
    const attempt = periodState.pending;
    if (!attempt || queries.candidateAnalytics.isFetching) return;
    if (
      queries.candidateAnalytics.authorityError ||
      queries.candidateAnalytics.error
    ) {
      dispatchPeriod({ type: "reject", attempt });
      return;
    }
    if (
      !candidateAnalyticsResponse ||
      queries.candidateAnalytics.isPlaceholderData
    ) {
      return;
    }
    dispatchPeriod({
      type:
        responseMatchesUser(user, candidateAnalyticsResponse) &&
        responseMatchesPeriod(candidateAnalyticsResponse, attempt.period)
          ? "commit"
          : "reject",
      attempt,
    });
  }, [
    candidateAnalyticsResponse,
    periodState.pending,
    queries.candidateAnalytics.authorityError,
    queries.candidateAnalytics.error,
    queries.candidateAnalytics.isFetching,
    queries.candidateAnalytics.isPlaceholderData,
    user,
  ]);

  const requestPeriod = (period: DashboardPeriodState) => {
    attemptID.current += 1;
    dispatchPeriod({
      type: "request",
      attempt: { id: attemptID.current, period },
    });
  };
  const retryFailedPeriod = () => {
    const failed = periodState.failed;
    if (!failed) return;
    attemptID.current += 1;
    dispatchPeriod({
      type: "request",
      attempt: { id: attemptID.current, period: failed.period },
    });
    void queries.candidateAnalytics.refetch();
  };
  const retryAnalytics = periodState.activePeriod
    ? () => void queries.activeAnalytics.refetch()
    : retryFailedPeriod;
  const retryOperations = () => void queries.operations.refetch();
  const dashboard =
    analytics && periodState.activePeriod
      ? renderRoleDashboard({
          user,
          analytics,
          operations,
          period: periodState.activePeriod,
          pendingPeriod: periodState.pending?.period ?? null,
          updating:
            queries.activeAnalytics.isFetching || periodState.pending !== null,
          onPeriodChange: requestPeriod,
          onNavigate: (destination) =>
            typeof destination === "string"
              ? void navigate({ to: destination })
              : void navigate(destination),
          onOpenPanel: setPanelRequest,
          onRetryAnalytics: retryAnalytics,
          onRetryOperations: retryOperations,
          selectedStoreId,
          authorizedStores,
          hasMoreStores: Boolean(storePages.hasNextPage),
          loadingMoreStores: storePages.isFetchingNextPage,
          onLoadMoreStores: () => void storePages.fetchNextPage(),
          onSelectStore: (id) => {
            if (!authorizedStores.some((store) => store.id === id)) return;
            setChosenStoreId(id);
            setPanel({ request: null, open: false });
          },
        })
      : null;

  return (
    <>
      {!dashboard &&
        user.role === "client_store" &&
        authorizedStores.length > 0 && (
          <div className="lnk209-super lnk209-client-preview lnk209-scope-fallback">
            <StoreSwitch
              stores={authorizedStores}
              selected={selectedStoreId ?? ""}
              onSelect={(id) => {
                if (!authorizedStores.some((store) => store.id === id)) return;
                setChosenStoreId(id);
                setPanel({ request: null, open: false });
              }}
              hasMore={Boolean(storePages.hasNextPage)}
              loadingMore={storePages.isFetchingNextPage}
              onLoadMore={() => void storePages.fetchNextPage()}
            />
          </div>
        )}
      <DashboardState
        analytics={{
          hasData: Boolean(analytics),
          isPending: periodState.activePeriod
            ? queries.activeAnalytics.isPending
            : queries.candidateAnalytics.isPending,
          isFetching:
            queries.activeAnalytics.isFetching ||
            (periodState.pending !== null &&
              queries.candidateAnalytics.isFetching),
          error:
            scopeError ??
            (periodState.activePeriod
              ? (queries.activeAnalytics.authorityError ??
                activeAnalyticsContractError ??
                queries.activeAnalytics.error)
              : queries.candidateAnalytics.isFetching
                ? null
                : (queries.candidateAnalytics.authorityError ??
                  candidateAnalyticsContractError ??
                  queries.candidateAnalytics.error)),
        }}
        operations={{
          hasData: Boolean(operations),
          isPending: queries.operations.isPending,
          isFetching: queries.operations.isFetching,
          error:
            scopeError ??
            queries.operations.authorityError ??
            operationsRoleError ??
            queries.operations.error,
        }}
        analyticsProjectionStatus={analytics?.projection_status}
        operationsProjectionStatus={operations?.projection_status}
        onRetryAnalytics={
          scopeError
            ? () => {
                void storePages.refetch();
              }
            : retryAnalytics
        }
        onRetryOperations={
          scopeError ? () => void storePages.refetch() : retryOperations
        }
        operationsContent={
          !analytics && operations ? (
            <DashboardOperationsSummary operations={operations} />
          ) : undefined
        }
      >
        {dashboard}
      </DashboardState>

      {periodState.failed && periodState.activePeriod ? (
        <FloatingToast
          title="Não foi possível mudar o período"
          message={`${dashboardPeriodLabel(periodState.failed.period.period)} não carregou. A visualização atual continua disponível.`}
          actionLabel="Tentar novamente"
          onAction={retryFailedPeriod}
          onDismiss={() => dispatchPeriod({ type: "dismiss" })}
        />
      ) : null}

      {panel.request && periodState.activePeriod ? (
        <DashboardDrilldownHost
          user={user}
          period={periodState.activePeriod}
          analytics={analytics}
          request={panel.request}
          open={panel.open}
          onRequestChange={setPanelRequest}
          onAfterClose={finishPanelClose}
        />
      ) : null}
    </>
  );
}
